import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Icon, TableCell, TableRow } from "@tremor/react";
import { DeleteModal, TableComponent, TitleTablePage } from "../components";
import { MenuLayout } from "../layouts";
import {
  deleteServiceFromApi,
  getServices,
  setDateForSearch, setModuleStatusForSearch,
} from '../../store/admin/thunks'
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/solid";
import { Spinner } from "../../ui";
import "./TableServicesPage.css";
import { getDateUTC } from "../helpers";

const headers = [
  "Fecha",
  "Cliente MX",
  "Cliente USD",
  "Factura",
  "Económico",
  "Pies",
  "Código Alpha",
  "código CAAT",
  "Placas",
  "Carta Porte",
  "Estatus del Modulo",
  "Estatus",
  "Número de Integración",
  "Numero de Pedimento",
  "Fitosanitario",
  "Báscula",
  "Permiso de sobrepeso",
  "Documentos",
  "Observaciones",
  "Acciones",
];

const RowTable = ({
  alphaCode,
  basculePermission,
  basculeValue,
  buyer,
  CAAT,
  client,
  compannyComment,
  creationDate,
  dispatch,
  driver,
  economicCode,
  feet,
  id,
  integrationNumber,
  invoiceNumber: name,
  manifest,
  moduleStatus,
  navigate,
  overweightPermission,
  overweightValue,
  phytosanitaryPermission,
  phytosanitaryValue,
  plateNumber,
  requirementNumber,
  servicesObservations,
  status,
  waybill,
}) => {
  const [showModal, setModal] = useState(false);

  const goToEdit = () => {
    navigate(`/admin/services/${id}`);
  };

  const date = new Date(creationDate);

  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // Los meses van de 0 a 11, así que sumamos 1
  const day = date.getUTCDate();
  const dateModification = `${year}-${String(month).padStart(2, "0")}-${String(
    day
  ).padStart(2, "0")}`;

  const documents = servicesObservations.map(
    (observations) => observations.observation.name
  );

  const observationsText = servicesObservations
    .filter(serviceObs => serviceObs.observation.name === "Otro")
    .map(serviceObs => serviceObs.customMessage)
    .join(", ");

  const deleteService = async () => {
    dispatch(deleteServiceFromApi(id));
  };

  const showModalDelete = () => {
    setModal(true);
  };

  const closeModalDelete = () => {
    setModal(false);
  };

  return (
    <>
      {showModal && (
        <DeleteModal
          information={`${name}`}
          cancelFunction={closeModalDelete}
          confirmFunction={deleteService}
        />
      )}
      <TableRow>
        <TableCell>{dateModification}</TableCell>
        <TableCell>{client.name}</TableCell>
        <TableCell>{buyer.name}</TableCell>
        <TableCell>{name}</TableCell>
        <TableCell>{economicCode}</TableCell>
        <TableCell>{feet}</TableCell>
        <TableCell>{alphaCode}</TableCell>
        <TableCell>{CAAT}</TableCell>
        <TableCell>{plateNumber}</TableCell>
        <TableCell>{waybill}</TableCell>
        <TableCell>{moduleStatus}</TableCell>
        <TableCell>{status.name}</TableCell>
        <TableCell>{integrationNumber}</TableCell>
        <TableCell>{requirementNumber}</TableCell>
        <TableCell>
          {phytosanitaryPermission ? <>Activo: {phytosanitaryValue} </> : <></>}
        </TableCell>

        <TableCell>
          {basculePermission ? <>Activo: {basculePermission} </> : <></>}
        </TableCell>
        <TableCell>
          {overweightPermission ? <>Activo: {overweightValue} </> : <></>}
        </TableCell>
        <TableCell>
          <ul>
            {documents.map((document, index) => (
              <li key={index}>{document}</li>
            ))}
          </ul>
        </TableCell>
        <TableCell>
          {observationsText || "Sin Comentarios"}
        </TableCell>
        <TableCell className="sticky-column">
          <Icon variant="solid" icon={PencilSquareIcon} onClick={goToEdit} />
          <Icon variant="solid" icon={TrashIcon} onClick={showModalDelete} />
        </TableCell>
      </TableRow>
    </>
  );
};

export const TableServicesPage = () => {
  const { services, isLoading, hasError, messageError, valueDate, valueModuleStatus } =
    useSelector((state) => state.admin);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tableContent = services.map((d) => (
    <RowTable key={d.id} {...d} navigate={navigate} dispatch={dispatch} />
  ));
  const [filters, setFilters] = useState({
    date: new Date(),
    dateValue: '0',
    status: 'ALL',
  })

  useEffect(() => {
    const fetchDate = async () => {
      const date = await getDateUTC();
      setFilters({ date: date });
    };
    fetchDate();
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      const date = await getDateUTC();
      dispatch(getServices(date));
    };
    fetchServices();
  }, [dispatch]);

  const getServicesByDate = (valueDate2, valueModuleStatus2, dateValue = "0") => {
    const statusVal = valueModuleStatus2 === 'ALL' ? '' : valueModuleStatus2;
    dispatch(getServices(valueDate2, statusVal, dateValue));
    dispatch(setDateForSearch(dateValue));
    dispatch(setModuleStatusForSearch(statusVal));
  };

  return (
    <>
      <MenuLayout hasError={hasError} messageError={messageError}>
        <TitleTablePage
          title="Servicios"
          titleBtn="Nuevo Servicio"
          urlToGo="admin/service"
        />
        {!isLoading ? (
          <TableComponent
            filters={filters}
            setFilters={setFilters}
            triggerFuncDate={getServicesByDate}
            showSelectLogs={true}
            showSearchInput={true}
            showDateSelect={true}
            showStatusSelect={true}
            showButtonXLS={true}
            title="Servicios Registrados"
            tableHeaders={headers}
            tableContent={tableContent}
            propNameSearch="economicCode"
          />
        ) : (
          <Spinner />
        )}
      </MenuLayout>
    </>
  );
};

RowTable.propTypes = {
  alphaCode: PropTypes.string,
  basculePermission: PropTypes.bool,
  basculeValue: PropTypes.number,
  buyer: PropTypes.object,
  CAAT: PropTypes.string,
  client: PropTypes.object,
  clientID: PropTypes.number,
  compannyComment: PropTypes.object,
  creationDate: PropTypes.string,
  date: PropTypes.string,
  dispatch: PropTypes.func,
  driver: PropTypes.object,
  economicCode: PropTypes.string,
  feet: PropTypes.string,
  id: PropTypes.number,
  integrationNumber: PropTypes.string,
  invoiceNumber: PropTypes.string,
  manifest: PropTypes.string,
  moduleStatus: PropTypes.string,
  navigate: PropTypes.func,
  overweightPermission: PropTypes.bool,
  phytosanitaryPermission: PropTypes.bool,
  phytosanitaryValue: PropTypes.number,
  plateNumber: PropTypes.string,
  requirementNumber: PropTypes.string,
  servicesObservations: PropTypes.array,
  status: PropTypes.object,
  updateStatus: PropTypes.bool,
  waybill: PropTypes.string,
};
